'use client'

import type * as React from 'react'

import * as SwitchPrimitive from '@radix-ui/react-switch'

import Icon, {
  type IconName,
  type PhosphorIconName,
} from '@/components/atomic/icon'
import { cn } from '@/utils/shadcn/utils'

const ToggleVariant = {
  DEFAULT: 'default',
  ICON: 'icon',
} as const

export type ToggleVariantType =
  (typeof ToggleVariant)[keyof typeof ToggleVariant]

interface SwitchProps
  extends React.ComponentProps<typeof SwitchPrimitive.Root> {
  variant?: ToggleVariantType
  icon?: IconName | PhosphorIconName
}

const DEFAULT_ROOT_STYLES = [
  'peer inline-flex h-5 w-9 shrink-0 items-center rounded-full border-2 border-transparent shadow-xs transition-all outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
  'data-[state=checked]:bg-moment-blue-grey-700 data-[state=unchecked]:bg-moment-blue-grey-200',
  'dark:data-[state=checked]:bg-moment-teal-400 dark:data-[state=unchecked]:bg-moment-blue-grey-700',
  'cursor-pointer',
] as const

const ICON_ROOT_STYLES = [
  'rounded-md !shadow-none border-none w-11',
  'data-[state=checked]:bg-moment-white-200 dark:data-[state=checked]:bg-moment-black-100',
  'data-[state=unchecked]:bg-moment-white-200 dark:data-[state=unchecked]:bg-moment-black-100',
] as const

const DEFAULT_THUMB_STYLES = [
  'pointer-events-none block size-4 rounded-full shadow-lg transition-transform',
  'bg-white data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0',
  'dark:bg-moment-black-100',
] as const

const ICON_THUMB_STYLES = [
  'flex items-center justify-center h-5 w-6 rounded-md shadow-none',
  'data-[state=checked]:bg-moment-red-300 dark:data-[state=checked]:bg-moment-red-800',
  'data-[state=unchecked]:bg-moment-green-400 dark:data-[state=unchecked]:bg-moment-green-900',
  'data-[state=checked]:text-moment-red-700 dark:data-[state=checked]:text-moment-red-200',
  'data-[state=unchecked]:text-moment-green-900 dark:data-[state=unchecked]:text-moment-green-200',
  'data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0',
] as const

function Switch({
  className,
  icon,
  variant = ToggleVariant.DEFAULT,
  ...props
}: SwitchProps) {
  const isIconVariant = variant === ToggleVariant.ICON

  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      className={cn(
        ...DEFAULT_ROOT_STYLES,
        isIconVariant && ICON_ROOT_STYLES,
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(
          ...DEFAULT_THUMB_STYLES,
          isIconVariant && ICON_THUMB_STYLES,
        )}
      >
        {isIconVariant && icon && (
          <Icon iconName={icon} size="medium" outline />
        )}
      </SwitchPrimitive.Thumb>
    </SwitchPrimitive.Root>
  )
}

export { Switch }
